import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import cls from 'clsx';
import { Loading, message } from '@linktivity/link-ui';
import { Calendar, Left } from '@linktivity/link-icons';

import {
  ConfirmBookingModal,
  SelectCalendar
} from '@link/china-railway/components';
import {
  OrderableTimeRange,
  RailwayTrainInfo
} from '@link/china-railway/services/types';
import { useStore } from '@link/china-railway/stores';
import { getLanguageCode } from '@link/china-railway/utils';

import { TrainItem } from './TrainItem';
import styles from './trainList.module.css';

const TrainListView = () => {
  const [loading, setLoading] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);
  const { date } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { chinaRailway } = useStore();
  const { bookingInfo, trainList, bookingId, token } = chinaRailway;
  const [selectedDate, setSelectedDate] = useState<string>(date || '');
  const [selectTimeRange, setSelectTimeRange] = useState<OrderableTimeRange>(
    {}
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedTrain, setSelectedTrain] = useState<RailwayTrainInfo | null>(
    null
  );
  const dateListRef = useRef<HTMLDivElement>(null);
  const timeSlotListRef = useRef<HTMLDivElement>(null);
  const timeRangesOptions = useMemo(() => {
    if (selectedDate === bookingInfo.planInfo?.orderableDateFrom) {
      return bookingInfo.planInfo?.firstOrderableDateTimeRanges || [];
    } else {
      return bookingInfo.planInfo?.orderableTimeRanges || [];
    }
  }, [
    bookingInfo.planInfo?.firstOrderableDateTimeRanges,
    bookingInfo.planInfo?.orderableDateFrom,
    bookingInfo.planInfo?.orderableTimeRanges,
    selectedDate
  ]);

  // 页面刷新后重新获取 bookingInfo
  useEffect(() => {
    if (!bookingInfo.planInfo && bookingId && token) {
      chinaRailway.getBookingData({
        bookingId,
        token,
        languageCode: getLanguageCode(i18n.language)
      });
    }
  }, [bookingInfo.planInfo, bookingId, token, chinaRailway, i18n.language]);

  useEffect(() => {
    if (!selectTimeRange.from && timeRangesOptions.length > 0) {
      setSelectTimeRange(timeRangesOptions[0] || {});
    }
  }, [selectTimeRange.from, timeRangesOptions]);

  useEffect(() => {
    if (selectTimeRange.from && selectTimeRange.to) {
      setLoading(true);
      chinaRailway
        .getTrainList({
          travelDate: selectedDate,
          bookingId: chinaRailway.bookingId,
          token: chinaRailway.token,
          orderTimeFrom: selectTimeRange.from,
          orderTimeTo: selectTimeRange.to
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectTimeRange.from, selectTimeRange.to, selectedDate, chinaRailway]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleBookTrain = useCallback((train: RailwayTrainInfo) => {
    setSelectedTrain(train);
    setConfirmModalOpen(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
    setSelectedTrain(null);
  }, []);

  const handleConfirmBooking = useCallback(() => {
    if (selectedTrain) {
      setConfirmModalLoading(true);
      chinaRailway
        .orderTicket({
          bookingId: chinaRailway.bookingId,
          token: chinaRailway.token,
          trainNo: selectedTrain.trainNumber,
          travelDate: selectedDate
        })
        .then(() => {
          message.success(t('confirmBooking.orderSuccess'));
          setTimeout(() => {
            navigate(
              `/order-detail/${chinaRailway.bookingId}/${chinaRailway.token}`
            );
          }, 1000);
        })
        .catch(() => {
          message.error(t('confirmBooking.orderFailed'));
        })
        .finally(() => {
          setConfirmModalLoading(false);
          setConfirmModalOpen(false);
          setSelectedTrain(null);
        });
    }
  }, [selectedTrain, chinaRailway, selectedDate, t, navigate]);

  const allDateOptions = useMemo(() => {
    return [...chinaRailway.dateOptions, ...chinaRailway.preDateOptions];
  }, [chinaRailway.dateOptions, chinaRailway.preDateOptions]);

  const isPreOrder = useMemo(() => {
    return chinaRailway.preDateOptions.some(
      option => option.value === selectedDate
    );
  }, [chinaRailway.preDateOptions, selectedDate]);

  const handleDateSelect = useCallback(
    (date: string) => {
      if (loading) return;
      setSelectedDate(date);
      // set default time range
      if (date === bookingInfo.planInfo?.orderableDateFrom) {
        setSelectTimeRange(
          bookingInfo.planInfo?.firstOrderableDateTimeRanges?.[0] || {}
        );
      } else {
        setSelectTimeRange(
          bookingInfo.planInfo?.orderableTimeRanges?.[0] || {}
        );
      }
      // scroll time slot list to start
      if (timeSlotListRef.current) {
        const firstElement = timeSlotListRef.current.querySelector('button');
        if (firstElement) {
          firstElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start'
          });
        }
      }
    },
    [
      bookingInfo.planInfo?.firstOrderableDateTimeRanges,
      bookingInfo.planInfo?.orderableDateFrom,
      bookingInfo.planInfo?.orderableTimeRanges,
      loading
    ]
  );

  const handleTimeRangeSelect = useCallback(
    (range: OrderableTimeRange) => {
      if (loading) return;
      setSelectTimeRange(range);
    },
    [loading]
  );

  const handleOpenCalendar = useCallback(() => {
    setCalendarOpen(true);
  }, []);

  const handleCloseCalendar = useCallback(() => {
    setCalendarOpen(false);
  }, []);

  const handleCalendarDateSelect = useCallback(
    (date: Date) => {
      const dateString = format(date, 'yyyy-MM-dd');
      handleDateSelect(dateString);
      setCalendarOpen(false);
    },
    [handleDateSelect]
  );

  // 自动居中选中的日期
  useEffect(() => {
    if (selectedDate && dateListRef.current) {
      const selectedElement = dateListRef.current.querySelector(
        `[data-date="${selectedDate}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [selectedDate]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <Left className={styles.backIcon} />
        </button>
        <h1 className={styles.title}>
          {bookingInfo.planInfo?.fromStationName}
          {' → '}
          {bookingInfo.planInfo?.toStationName}
        </h1>
      </div>

      <div className={styles.dateScrollContainer}>
        <div className={styles.dateList} ref={dateListRef}>
          {allDateOptions.map(option => {
            const isSelected = selectedDate === option.value;
            const isOptionPreOrder = chinaRailway.preDateOptions.some(
              o => o.value === option.value
            );
            return (
              <button
                key={option.value}
                data-date={option.value}
                className={cls(styles.dateItem, {
                  [styles.dateItemActive]: isSelected && !isOptionPreOrder,
                  [styles.dateItemActiveWarning]: isSelected && isOptionPreOrder
                })}
                onClick={() => handleDateSelect(option.value)}
              >
                <div>{option.label}</div>
                <div>{option.weekday}</div>
              </button>
            );
          })}
        </div>
        <button
          className={styles.calendarIconContainer}
          onClick={handleOpenCalendar}
          aria-label={t('views.railway.selectDate')}
        >
          <Calendar className={styles.calendarIcon} />
        </button>
      </div>

      <div className={styles.timeSlotContainer}>
        <div className={styles.timeSlotList} ref={timeSlotListRef}>
          {timeRangesOptions?.map((range, index) => {
            const isSelected = selectTimeRange.from === range.from;
            return (
              <button
                key={index}
                className={cls(styles.timeSlotButton, {
                  [styles.timeSlotButtonActive]: isSelected && !isPreOrder,
                  [styles.timeSlotButtonActiveWarning]: isSelected && isPreOrder
                })}
                onClick={() => handleTimeRangeSelect(range)}
              >
                {range.from} - {range.to}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Loading />
        </div>
      ) : (
        <div className={styles.trainList}>
          {trainList.map((train, index) => (
            <TrainItem
              key={index}
              train={train}
              isPreOrder={isPreOrder}
              onBook={handleBookTrain}
            />
          ))}

          {trainList.length === 0 && (
            <div className={styles.emptyState}>
              <p>{t('views.trainList.noTrains')}</p>
            </div>
          )}
        </div>
      )}

      <SelectCalendar
        open={calendarOpen}
        onClose={handleCloseCalendar}
        title={t('views.railway.selectDate')}
        value={selectedDate ? parseISO(selectedDate) : undefined}
        onChange={handleCalendarDateSelect}
        dateOptions={chinaRailway.calendarDateOptions}
        preDateOptions={chinaRailway.calendarPreDateOptions}
      />

      <ConfirmBookingModal
        open={confirmModalOpen}
        isPreOrder={isPreOrder}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmBooking}
        train={selectedTrain}
        loading={confirmModalLoading}
        travelDate={selectedDate}
        seatClass={bookingInfo.planInfo?.seatClass}
        passengerName={bookingInfo.orderInfo?.passengers?.[0]?.name}
        passengerCount={bookingInfo.orderInfo?.passengers?.length}
      />
    </div>
  );
};

const TrainList = observer(TrainListView);

export default TrainList;
